import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatDate';
import { creditsToYuan } from '../utils/currency';
import { User, DemoTemplate, JobRole } from '../types';
import { adminListUsers, adminCreateUser, adminCreateAdmin, adminUpdateUserPassword, adminUpdateUserRole, adminUpdateUserPermissions, adminUpdateUserPhone, adminUpdateUserJobRole, adminUpdateUserNote, adminDeleteUser, banUser, unbanUser, adminRechargeCredits, adminDeductCredits, adminGetRecharges, adminGetConsumptions, listDemoTemplates, listAccountGroups, AccountGroupWithCount } from '../api';
import JobRoleSelect from '../components/JobRoleSelect';
import { demoConfirmMessage } from './admin/demoConfirm';
import { CreditRecharge, CreditConsumption, AdminPermission } from '../types';
import { isAdminTier, isSuperAdmin } from '../utils/permissions';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

const PAGE_SIZE = 20;
const ROLE_ADMIN: User['role'] = 'admin';

const AdminUsers: React.FC = () => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState({ username: '', password: '', phone: '', asAdmin: false, permissions: [] as AdminPermission[], accountGroupId: '', jobRole: 'generator' as JobRole, note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accountGroups, setAccountGroups] = useState<AccountGroupWithCount[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const loadAccountGroups = useCallback(() => {
    setGroupsLoading(true);
    listAccountGroups()
      .then(r => setAccountGroups(r.groups))
      .catch(() => toast.error(t('users.loadGroupsFailed')))
      .finally(() => setGroupsLoading(false));
  }, [toast, t]);

  // password reset modal
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdTarget, setPwdTarget] = useState<User | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [pwdError, setPwdError] = useState('');

  // phone edit modal
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneTarget, setPhoneTarget] = useState<User | null>(null);
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // note edit modal
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteTarget, setNoteTarget] = useState<User | null>(null);
  const [newNote, setNewNote] = useState('');
  const [noteError, setNoteError] = useState('');

  // recharge modal
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeTarget, setRechargeTarget] = useState<User | null>(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeRemark, setRechargeRemark] = useState('');
  const [rechargeError, setRechargeError] = useState('');
  const [recharging, setRecharging] = useState(false);

  // deduct modal
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [deductTarget, setDeductTarget] = useState<User | null>(null);
  const [deductAmount, setDeductAmount] = useState('');
  const [deductRemark, setDeductRemark] = useState('');
  const [deductAllowNegative, setDeductAllowNegative] = useState(false);
  const [deductError, setDeductError] = useState('');
  const [deducting, setDeducting] = useState(false);
  const [confirmDeduct, setConfirmDeduct] = useState(false);

  // records modal
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [recordsTarget, setRecordsTarget] = useState<User | null>(null);
  const [recordsTab, setRecordsTab] = useState<'recharge' | 'consumption'>('recharge');
  const [rechargeRecords, setRechargeRecords] = useState<CreditRecharge[]>([]);
  const [consumptionRecords, setConsumptionRecords] = useState<CreditConsumption[]>([]);
  const [consumptionPage, setConsumptionPage] = useState(1);
  const [consumptionTotal, setConsumptionTotal] = useState(0);
  const [rechargePage, setRechargePage] = useState(1);
  const [rechargeTotal, setRechargeTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // confirm dialogs
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [confirmBan, setConfirmBan] = useState(false);
  const [banTarget, setBanTarget] = useState<User | null>(null);

  // manage role & permissions modal (super admin only)
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageTarget, setManageTarget] = useState<User | null>(null);
  const [manageRole, setManageRole] = useState<User['role']>('user');
  const [managePerms, setManagePerms] = useState<AdminPermission[]>([]);
  const [manageSaving, setManageSaving] = useState(false);
  const [manageError, setManageError] = useState('');

  // demo templates（建号时可选克隆的示例项目）
  const [demoTemplates, setDemoTemplates] = useState<DemoTemplate[]>([]);
  const [selectedDemoTemplateId, setSelectedDemoTemplateId] = useState<number>(0);
  const [demoTemplatesError, setDemoTemplatesError] = useState('');
  const [showDemoConfirm, setShowDemoConfirm] = useState(false);

  const loadDemoTemplates = useCallback(async () => {
    try {
      setDemoTemplates(await listDemoTemplates());
      setDemoTemplatesError('');
    } catch (e: any) {
      // 不静默当作"没有模板"：显式报错，让管理员知道下拉不完整、可重试。
      // 但不阻断建普通用户（demo 是可选增强，默认"不启用"仍安全）。
      setDemoTemplates([]);
      setDemoTemplatesError(e?.message || '示例模板列表加载失败');
    }
  }, []);
  useEffect(() => { loadDemoTemplates(); }, [loadDemoTemplates]);

  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const amSuper = isSuperAdmin(currentUser);
  const PERMISSION_GROUPS: AdminPermission[] = ['platform_config', 'operations', 'billing', 'monitoring'];
  // 与后端一致:非超管对管理员账号只读
  const rowLocked = (u: User) => isAdminTier(u) && !amSuper;
  const isDashi = (u: User) => u.username === 'dashi';
  // 只有 admin(非超管)展示权限组小字
  const isPlainAdmin = (u: User) => isAdminTier(u) && !isSuperAdmin(u);

  const load = useCallback(async (page = usersPage, username = searchUsername, phone = searchPhone, userId = searchUserId) => {
    try {
      setLoading(true);
      const res = await adminListUsers(page, PAGE_SIZE, username, phone, userId);
      setUsers(res.items || []);
      setUsersTotal(res.total || 0);
      setUsersPage(page);
    } finally {
      setLoading(false);
    }
  }, [usersPage, searchUsername, searchPhone, searchUserId]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hasInitializing = users.some(u => u.initStatus === 'initializing');
    if (!hasInitializing) return;
    const timer = setInterval(async () => {
      try {
        const res = await adminListUsers(usersPage, PAGE_SIZE, searchUsername, searchPhone, searchUserId);
        setUsers(res.items || []);
        setUsersTotal(res.total || 0);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(timer);
  }, [users, usersPage, searchUsername, searchPhone, searchUserId]);

  const handleSearch = () => {
    load(1, searchUsername, searchPhone, searchUserId);
  };

  const handleReset = () => {
    setSearchUsername('');
    setSearchPhone('');
    setSearchUserId('');
    load(1, '', '', '');
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm({ username: '', password: '', phone: '', asAdmin: false, permissions: [], accountGroupId: '', jobRole: 'generator' as JobRole, note: '' });
    setSelectedDemoTemplateId(0);
    setError('');
    loadAccountGroups();
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.username.trim()) { setError(t('users.usernameEmpty')); return; }
    if (!/^[a-zA-Z0-9]+$/.test(form.username.trim())) { setError(t('users.usernameAlphanumeric')); return; }
    if (!editingUser && !form.password.trim()) { setError(t('users.passwordEmpty')); return; }
    if (!form.phone.trim()) { setError(t('users.phoneEmpty')); return; }
    if (!/^1[3-9]\d{9}$/.test(form.phone.trim())) { setError(t('users.phoneInvalid')); return; }
    if (!form.asAdmin && !form.accountGroupId) { setError(t('users.accountGroupRequired')); return; }
    if (Array.from(form.note.trim()).length > 100) { setError(t('users.noteTooLong')); return; }
    setError('');
    if (form.asAdmin) {
      setSaving(true);
      try {
        await adminCreateAdmin(form.username.trim(), form.password, form.phone.trim(), form.permissions, form.note.trim());
        setShowModal(false);
        load(usersPage, searchUsername, searchPhone);
      } catch (err: any) {
        setError(err.message || t('users.createFailed'));
      } finally {
        setSaving(false);
      }
      return;
    }
    // 普通用户：弹自定义确认框（含示例模板二次确认文案），确认后再创建
    setShowDemoConfirm(true);
  };

  const confirmedCreateUser = async () => {
    setSaving(true);
    setError('');
    try {
      await adminCreateUser(form.username.trim(), form.password, form.phone.trim(), form.accountGroupId, selectedDemoTemplateId, form.jobRole, form.note.trim());
      setShowDemoConfirm(false);
      setShowModal(false);
      load(usersPage, searchUsername, searchPhone);
    } catch (err: any) {
      // 关掉确认框，让错误回到建号表单上显示
      setShowDemoConfirm(false);
      setError(err.message || t('users.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleJobRoleChange = async (user: User, next: JobRole) => {
    const prev: JobRole = user.jobRole ?? 'generator';
    if (next === prev) return;
    setUsers(list => list.map(u => u.id === user.id ? { ...u, jobRole: next } : u));
    try {
      await adminUpdateUserJobRole(user.id, next);
      toast.success(t('users.jobRoleUpdated'));
    } catch (err: any) {
      setUsers(list => list.map(u => u.id === user.id ? { ...u, jobRole: prev } : u));
      toast.error(err.message || t('users.jobRoleUpdateFailed'));
    }
  };

  const openManage = (u: User) => {
    setManageTarget(u);
    setManageRole(u.role);
    setManagePerms((u.permissions ?? []) as AdminPermission[]);
    setManageError('');
    setShowManageModal(true);
  };

  const handleManageSave = async () => {
    if (!manageTarget) return;
    setManageSaving(true);
    setManageError('');
    try {
      if (manageRole !== manageTarget.role) {
        await adminUpdateUserRole(manageTarget.id, manageRole);
      }
      if (manageRole === ROLE_ADMIN) {
        await adminUpdateUserPermissions(manageTarget.id, managePerms);
      }
      setShowManageModal(false);
      load(usersPage, searchUsername, searchPhone);
    } catch (err: any) {
      setManageError(err.message || t('users.updateFailed'));
    } finally {
      setManageSaving(false);
    }
  };

  const handleDelete = (user: User) => {
    if (user.id === currentUser?.id) { toast.warning(t('users.cannotDeleteSelf')); return; }
    setDeleteTarget(user);
    setConfirmDelete(true);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminDeleteUser(deleteTarget.id);
      const maxPage = Math.max(1, Math.ceil((usersTotal - 1) / PAGE_SIZE));
      load(Math.min(usersPage, maxPage), searchUsername, searchPhone);
    } catch (err: any) {
      toast.error(err.message || t('users.deleteFailed'));
    } finally {
      setConfirmDelete(false);
      setDeleteTarget(null);
    }
  };

  const openBan = (user: User) => { setBanTarget(user); setConfirmBan(true); };

  const doBan = async () => {
    if (!banTarget) return;
    try {
      const res = await banUser(banTarget.id);
      toast.success(res.creditsCleared ? t('users.banSuccess') : t('users.banCreditsManual'));
      load(usersPage, searchUsername, searchPhone);
    } catch (err: any) {
      toast.error(err.message || t('users.banFailed'));
    } finally {
      setConfirmBan(false);
      setBanTarget(null);
    }
  };

  const doUnban = async (user: User) => {
    try {
      await unbanUser(user.id);
      toast.success(t('users.unbanSuccess'));
      load(usersPage, searchUsername, searchPhone);
    } catch (err: any) {
      toast.error(err.message || t('users.banFailed'));
    }
  };

  const openResetPwd = (user: User) => {
    setPwdTarget(user);
    setNewPwd('');
    setPwdError('');
    setShowPwdModal(true);
  };

  const handleResetPwd = async () => {
    if (!newPwd.trim()) { setPwdError(t('users.passwordEmpty')); return; }
    try {
      await adminUpdateUserPassword(pwdTarget!.id, newPwd);
      setShowPwdModal(false);
    } catch (err: any) {
      setPwdError(err.message || t('users.modifyFailed'));
    }
  };

  const openEditPhone = (user: User) => {
    setPhoneTarget(user);
    setNewPhone(user.phone || '');
    setPhoneError('');
    setShowPhoneModal(true);
  };

  const handleEditPhone = async () => {
    if (!phoneTarget) return;
    if (!newPhone.trim()) { setPhoneError(t('users.phoneEmpty')); return; }
    if (!/^1[3-9]\d{9}$/.test(newPhone.trim())) { setPhoneError(t('users.phoneInvalid')); return; }
    try {
      await adminUpdateUserPhone(phoneTarget.id, newPhone.trim());
      setUsers(prev => prev.map(u => u.id === phoneTarget.id ? { ...u, phone: newPhone.trim() } : u));
      setShowPhoneModal(false);
    } catch (err: any) {
      setPhoneError(err.message || t('users.modifyFailed'));
    }
  };

  const openEditNote = (user: User) => {
    setNoteTarget(user);
    setNewNote(user.note || '');
    setNoteError('');
    setShowNoteModal(true);
  };

  const handleEditNote = async () => {
    if (!noteTarget) return;
    const note = newNote.trim();
    if (Array.from(note).length > 100) { setNoteError(t('users.noteTooLong')); return; }
    try {
      await adminUpdateUserNote(noteTarget.id, note);
      setUsers(prev => prev.map(u => u.id === noteTarget.id ? { ...u, note } : u));
      setShowNoteModal(false);
    } catch (err: any) {
      setNoteError(err.message || t('users.modifyFailed'));
    }
  };

  const openRecharge = (user: User) => {
    setRechargeTarget(user);
    setRechargeAmount('');
    setRechargeRemark('');
    setRechargeError('');
    setShowRechargeModal(true);
  };

  const handleRecharge = async () => {
    const yuan = parseFloat(rechargeAmount);
    if (!yuan || yuan <= 0) { setRechargeError(t('users.positiveAmountRequired')); return; }
    const credits = Math.round(yuan * 100);
    if (!rechargeTarget) return;
    setRecharging(true);
    setRechargeError('');
    try {
      const result = await adminRechargeCredits(rechargeTarget.id, credits, rechargeRemark.trim());
      setUsers(prev => prev.map(u => u.id === rechargeTarget.id ? { ...u, credits: result.balanceAfter } : u));
      setShowRechargeModal(false);
      toast.success(t('users.rechargeSuccess', { username: rechargeTarget.username, amount: yuan }));
    } catch (err: any) {
      setRechargeError(err.message || t('users.rechargeFailed'));
    } finally {
      setRecharging(false);
    }
  };

  const openDeduct = (user: User) => {
    setDeductTarget(user);
    setDeductAmount('');
    setDeductRemark('');
    setDeductAllowNegative(false);
    setDeductError('');
    setShowDeductModal(true);
  };

  const handleDeductSubmit = () => {
    const yuan = parseFloat(deductAmount);
    if (!yuan || yuan <= 0) { setDeductError(t('users.positiveAmountRequired')); return; }
    if (!deductRemark.trim()) { setDeductError(t('users.deductReasonRequired')); return; }
    if (!deductTarget) return;
    const currentBalance = deductTarget.credits ?? 0;
    const credits = Math.round(yuan * 100);
    if (!deductAllowNegative && currentBalance < credits) {
      setDeductError(t('users.insufficientBalance'));
      return;
    }
    setDeductError('');
    setConfirmDeduct(true);
  };

  const doDeduct = async () => {
    const yuan = parseFloat(deductAmount);
    const amount = Math.round(yuan * 100);
    if (!amount || amount <= 0 || !deductTarget) {
      setConfirmDeduct(false);
      return;
    }
    setDeducting(true);
    setDeductError('');
    try {
      const result = await adminDeductCredits(deductTarget.id, amount, deductRemark.trim(), deductAllowNegative);
      setUsers(prev => prev.map(u => u.id === deductTarget.id ? { ...u, credits: result.balanceAfter } : u));
      setConfirmDeduct(false);
      setShowDeductModal(false);
      toast.success(t('users.deductSuccess', { username: deductTarget.username, amount: yuan }));
    } catch (err: any) {
      setConfirmDeduct(false);
      setDeductError(err.message || t('users.deductFailed'));
    } finally {
      setDeducting(false);
    }
  };

  const loadRecharges = useCallback(async (userId: string, page: number) => {
    setRecordsLoading(true);
    try {
      const rech = await adminGetRecharges(userId, page, PAGE_SIZE);
      setRechargeRecords(rech.items || []);
      setRechargeTotal(rech.total || 0);
      setRechargePage(page);
    } catch {
      toast.error(t('users.loadRechargesFailed'));
    } finally {
      setRecordsLoading(false);
    }
  }, [toast, t]);

  const loadConsumptions = useCallback(async (userId: string, page: number) => {
    setRecordsLoading(true);
    try {
      const cons = await adminGetConsumptions(userId, page, PAGE_SIZE);
      setConsumptionRecords(cons.items || []);
      setConsumptionTotal(cons.total || 0);
      setConsumptionPage(page);
    } catch {
      toast.error(t('users.loadConsumptionsFailed'));
    } finally {
      setRecordsLoading(false);
    }
  }, [toast, t]);

  const openRecords = async (user: User) => {
    setRecordsTarget(user);
    setRecordsTab('recharge');
    setShowRecordsModal(true);
    setRechargePage(1);
    setConsumptionPage(1);
    setRecordsLoading(true);
    try {
      const [rech, cons] = await Promise.all([
        adminGetRecharges(user.id, 1, PAGE_SIZE),
        adminGetConsumptions(user.id, 1, PAGE_SIZE),
      ]);
      setRechargeRecords(rech.items || []);
      setRechargeTotal(rech.total || 0);
      setConsumptionRecords(cons.items || []);
      setConsumptionTotal(cons.total || 0);
    } catch {
      toast.error(t('users.loadRecordsFailed'));
    } finally {
      setRecordsLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('users.breadcrumbHome'), to: '/projects' }, { label: t('users.title') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('users.title')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('users.createUser')}</button>
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="input"
          value={searchUsername}
          onChange={e => setSearchUsername(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={t('users.searchUsername')}
          style={{ width: '160px' }}
        />
        <input
          className="input"
          value={searchPhone}
          onChange={e => setSearchPhone(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={t('users.searchPhone')}
          style={{ width: '160px' }}
        />
        <input
          className="input"
          value={searchUserId}
          onChange={e => setSearchUserId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={t('users.searchUserId')}
          style={{ width: '220px', fontFamily: 'monospace', fontSize: '0.8rem' }}
        />
        <button className="btn btn-primary" onClick={handleSearch} style={{ fontSize: '0.85rem' }}>{t('users.search')}</button>
        {(searchUsername || searchPhone || searchUserId) && (
          <button className="btn btn-secondary" onClick={handleReset} style={{ fontSize: '0.85rem' }}>{t('users.reset')}</button>
        )}
        <span style={{ fontSize: '0.82rem', color: 'var(--text-3)', marginLeft: 'auto' }}>
          {t('users.totalUsers', { total: usersTotal })}
        </span>
      </div>

      {loading ? <p>{t('loading', { ns: 'common' })}</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {users.map(user => (
            <div key={user.id} className="list-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', rowGap: '0.3rem' }}>
                <span style={{ fontWeight: 600 }}>{user.username}</span>
                <span style={{
                  background: isAdminTier(user) ? 'var(--primary)' : 'var(--border)',
                  color: isAdminTier(user) ? '#fff' : 'var(--text-2)',
                  fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px'
                }}>
                  {({ super_admin: t('users.superAdmin'), admin: t('users.admin'), user: t('users.normalUser') } as Record<string, string>)[user.role]}
                </span>
                {isPlainAdmin(user) && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                    {(user.permissions ?? []).length ? (user.permissions ?? []).map(g => t(`users.perm.${g}`)).join(' / ') : t('users.noPermissions')}
                  </span>
                )}
                {user.id === currentUser?.id && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--primary)' }}>{t('users.me')}</span>
                )}
                {user.bannedAt && (
                  <span style={{ fontSize: '0.7rem', background: 'var(--danger)', color: '#fff', padding: '1px 6px', borderRadius: '10px' }}>{t('users.banned')}</span>
                )}
                <span style={{ width: '1px', height: '14px', background: 'var(--border)' }} aria-hidden />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{user.phone || t('users.phoneNotSet')}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('users.createdAt', { date: formatDate(user.createdAt, false) })}</span>
                <span style={{
                  fontSize: '0.8rem', fontWeight: 600,
                  color: (user.credits ?? 0) < 1000 ? 'var(--danger)' : 'var(--success)',
                }}>
                  {t('users.balance', { amount: creditsToYuan(user.credits ?? 0) })}
                </span>
                {user.initStatus === 'initializing' && (
                  <span style={{ fontSize: '0.75rem', background: '#fef3cd', color: '#856404', padding: '2px 6px', borderRadius: '4px' }}>初始化中</span>
                )}
                {user.initStatus === 'failed' && (
                  <span style={{ fontSize: '0.75rem', background: '#f8d7da', color: '#721c24', padding: '2px 6px', borderRadius: '4px' }}>初始化失败</span>
                )}
                {user.note && (
                  <>
                    <span style={{ width: '1px', height: '14px', background: 'var(--border)' }} aria-hidden />
                    <span title={user.note} style={{ fontSize: '0.8rem', color: 'var(--text-3)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t('users.note')}: {user.note}
                    </span>
                  </>
                )}
              </div>
              <div className="list-item-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <JobRoleSelect value={user.jobRole ?? 'generator'} disabled={rowLocked(user)} style={{ width: 'auto', fontSize: '0.8rem', padding: '2px 6px' }} onChange={next => handleJobRoleChange(user, next)} />
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button className="btn btn-ghost" style={actionBtnStyle('var(--warning)')} onClick={() => openRecharge(user)} disabled={rowLocked(user)}>{t('users.recharge')}</button>
                  <button className="btn btn-ghost" style={actionBtnStyle('var(--danger)')} onClick={() => openDeduct(user)} disabled={rowLocked(user)}>{t('users.deduct')}</button>
                  <button className="btn btn-ghost" style={actionBtnStyle()} onClick={() => openRecords(user)}>{t('users.records')}</button>
                </div>
                <span style={{ width: '1px', height: '16px', background: 'var(--border)' }} aria-hidden />
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button className="btn btn-ghost" style={actionBtnStyle()} onClick={() => openEditPhone(user)} disabled={rowLocked(user)}>{t('users.phone')}</button>
                  <button className="btn btn-ghost" style={actionBtnStyle()} onClick={() => openEditNote(user)} disabled={rowLocked(user)}>{t('users.note')}</button>
                  <button className="btn btn-ghost" style={actionBtnStyle()} onClick={() => openResetPwd(user)} disabled={rowLocked(user)}>{t('users.password')}</button>
                  <button
                    className="btn btn-ghost"
                    style={actionBtnStyle(user.bannedAt ? 'var(--success)' : 'var(--danger)')}
                    onClick={() => (user.bannedAt ? doUnban(user) : openBan(user))}
                    disabled={user.id === currentUser?.id || rowLocked(user) || isDashi(user)}
                  >
                    {user.bannedAt ? t('users.unban') : t('users.ban')}
                  </button>
                  {amSuper && (
                    <button
                      className="btn btn-ghost"
                      style={actionBtnStyle(user.id === currentUser?.id || isDashi(user) ? 'var(--text-3)' : 'var(--primary)')}
                      onClick={() => openManage(user)}
                      disabled={user.id === currentUser?.id || isDashi(user)}
                    >
                      {t('users.manageRole')}
                    </button>
                  )}
                </div>
                <span style={{ width: '1px', height: '16px', background: 'var(--border)' }} aria-hidden />
                <button
                  className="btn btn-danger"
                  style={actionBtnStyle()}
                  onClick={() => handleDelete(user)}
                  disabled={user.id === currentUser?.id || rowLocked(user) || isDashi(user)}
                >
                  {t('users.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {usersTotal > PAGE_SIZE && (
        <PaginationBar
          page={usersPage}
          totalPages={Math.ceil(usersTotal / PAGE_SIZE)}
          loading={loading}
          onPageChange={(p) => load(p, searchUsername, searchPhone)}
          prevLabel={t('users.prevPage')}
          nextLabel={t('users.nextPage')}
        />
      )}

      {/* Create user modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={t('users.createUserTitle')}
        width="400px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('users.creating') : t('users.create')}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('users.username')}</label>
          <input className="input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder={t('users.usernamePlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.passwordLabel')}</label>
          <input className="input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={t('users.passwordPlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.phoneLabel')}</label>
          <input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder={t('users.phonePlaceholder')} maxLength={11} />
        </div>
        {!form.asAdmin && (
          <div className="form-group">
            <label className="form-label">{t('users.accountGroup')}</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select className="input" style={{ flex: 1 }} value={form.accountGroupId} onChange={e => setForm({ ...form, accountGroupId: e.target.value })}>
                <option value="">{t('users.accountGroupPlaceholder')}</option>
                {accountGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button type="button" className="btn btn-ghost btn-sm" title={t('users.refreshGroups')} disabled={groupsLoading} onClick={loadAccountGroups}>⟳</button>
            </div>
            <a href="/admin/account-groups" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.78rem', color: 'var(--primary)', textDecoration: 'none', marginTop: '0.3rem', display: 'inline-block' }}>
              {t('users.manageGroups')}
            </a>
          </div>
        )}
        {!form.asAdmin && (
          <div className="form-group">
            <label className="form-label">示例模板</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select className="input" style={{ flex: 1 }} value={selectedDemoTemplateId}
                onChange={e => setSelectedDemoTemplateId(Number(e.target.value))}>
                <option value={0}>不启用示例模板</option>
                {demoTemplates.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>{tpl.id} - {tpl.nameTemplate}</option>
                ))}
              </select>
              {demoTemplatesError && (
                <button type="button" className="btn btn-ghost btn-sm" title="重试" onClick={loadDemoTemplates}>⟳</button>
              )}
            </div>
            {demoTemplatesError && (
              <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: '0.3rem' }}>
                {demoTemplatesError}（点 ⟳ 重试）
              </div>
            )}
          </div>
        )}
        {!form.asAdmin && (
          <div className="form-group">
            <label className="form-label">{t('users.jobRole')}</label>
            <JobRoleSelect value={form.jobRole} onChange={next => setForm({ ...form, jobRole: next })} />
          </div>
        )}
        <div className="form-group">
          <label className="form-label">{t('users.noteLabel')}</label>
          {/* maxLength 只作防巨量粘贴兜底(UTF-16 口径);真正的 100 字限制靠计数器+提交校验+后端 400 */}
          <textarea className="input" rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} maxLength={200} style={{ resize: 'vertical' }} />
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 4 }}>{Array.from(form.note).length}/100</div>
        </div>
        {amSuper && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={form.asAdmin} onChange={e => setForm({ ...form, asAdmin: e.target.checked })} />
              {t('users.createAsAdmin')}
            </label>
            {form.asAdmin && PERMISSION_GROUPS.map(g => (
              <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginTop: '0.3rem', marginLeft: '1.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.permissions.includes(g)}
                  onChange={e => setForm({ ...form, permissions: e.target.checked ? [...form.permissions, g] : form.permissions.filter(x => x !== g) })} />
                {t(`users.perm.${g}`)}
              </label>
            ))}
          </div>
        )}
        {error && <p className="error-text">{error}</p>}
      </Modal>

      {/* 建普通用户的示例模板二次确认（自定义弹窗，非原生 confirm） */}
      <ConfirmDialog
        open={showDemoConfirm}
        onCancel={() => setShowDemoConfirm(false)}
        onConfirm={confirmedCreateUser}
        title="确认创建用户"
        message={demoConfirmMessage(selectedDemoTemplateId, demoTemplates)}
        loading={saving}
      />

      {/* Manage role & permissions modal (super admin only) */}
      <Modal
        open={showManageModal && !!manageTarget}
        onClose={() => setShowManageModal(false)}
        title={t('users.manageRoleTitle', { username: manageTarget?.username || '' })}
        width="420px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowManageModal(false)} disabled={manageSaving}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleManageSave} disabled={manageSaving}>{manageSaving ? '...' : t('confirm', { ns: 'common' })}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('users.role')}</label>
          <select className="input" value={manageRole} onChange={e => setManageRole(e.target.value as User['role'])}>
            <option value="user">{t('users.normalUser')}</option>
            <option value="admin">{t('users.admin')}</option>
            <option value="super_admin">{t('users.superAdmin')}</option>
          </select>
        </div>
        {manageRole === ROLE_ADMIN && (
          <div className="form-group">
            <label className="form-label">{t('users.permissionGroups')}</label>
            {PERMISSION_GROUPS.map(g => (
              <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginTop: '0.3rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={managePerms.includes(g)}
                  onChange={e => setManagePerms(prev => e.target.checked ? [...prev, g] : prev.filter(x => x !== g))} />
                {t(`users.perm.${g}`)}
              </label>
            ))}
          </div>
        )}
        {manageError && <p className="error-text">{manageError}</p>}
      </Modal>

      {/* Reset password modal */}
      <Modal
        open={showPwdModal && !!pwdTarget}
        onClose={() => setShowPwdModal(false)}
        title={t('users.resetPasswordTitle', { username: pwdTarget?.username || '' })}
        width="360px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowPwdModal(false)}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleResetPwd}>{t('confirm', { ns: 'common' })}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('users.newPasswordLabel')}</label>
          <input className="input" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder={t('users.newPasswordPlaceholder')} autoFocus />
        </div>
        {pwdError && <p className="error-text">{pwdError}</p>}
      </Modal>

      {/* Edit phone modal */}
      <Modal
        open={showPhoneModal && !!phoneTarget}
        onClose={() => setShowPhoneModal(false)}
        title={t('users.editPhoneTitle', { username: phoneTarget?.username || '' })}
        width="360px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowPhoneModal(false)}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleEditPhone}>{t('confirm', { ns: 'common' })}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('users.phoneLabel')}</label>
          <input className="input" value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder={t('users.phonePlaceholder')} maxLength={11} autoFocus />
        </div>
        {phoneError && <p className="error-text">{phoneError}</p>}
      </Modal>

      {/* Edit note modal */}
      <Modal
        open={showNoteModal && !!noteTarget}
        onClose={() => setShowNoteModal(false)}
        title={t('users.editNoteTitle', { username: noteTarget?.username || '' })}
        width="420px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowNoteModal(false)}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleEditNote} disabled={Array.from(newNote.trim()).length > 100}>{t('confirm', { ns: 'common' })}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('users.noteLabel')}</label>
          {/* maxLength 只作防巨量粘贴兜底,不能设 100:HTML maxLength 按 UTF-16 计,emoji 算 2,
              设 100 会出现计数器未满却打不进字;真正的 100 字限制靠计数器+按钮禁用+后端 400 */}
          <textarea className="input" rows={3} value={newNote} onChange={e => setNewNote(e.target.value)} maxLength={200} autoFocus style={{ resize: 'vertical' }} />
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 4 }}>{Array.from(newNote).length}/100</div>
        </div>
        {noteError && <p className="error-text">{noteError}</p>}
      </Modal>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => { setConfirmDelete(false); setDeleteTarget(null); }}
        onConfirm={doDelete}
        title={t('users.deleteUserTitle')}
        message={t('users.deleteUserMessage', { username: deleteTarget?.username || '' })}
        confirmText={t('users.confirmDelete')}
        confirmDanger
      />

      {/* Confirm ban */}
      <ConfirmDialog
        open={confirmBan}
        onCancel={() => { setConfirmBan(false); setBanTarget(null); }}
        onConfirm={doBan}
        title={t('users.banUserTitle')}
        message={t('users.banUserMessage', { username: banTarget?.username || '' })}
        confirmText={t('users.confirmBan')}
        confirmDanger
      />

      {/* Recharge modal */}
      <Modal
        open={showRechargeModal && !!rechargeTarget}
        onClose={() => setShowRechargeModal(false)}
        title={t('users.rechargeTitle', { username: rechargeTarget?.username || '' })}
        width="400px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRechargeModal(false)} disabled={recharging}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleRecharge} disabled={recharging}>{recharging ? t('users.recharging') : t('users.confirmRecharge')}</button>
          </>
        }
      >
        <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-2)' }}>
          {t('users.currentBalance')} <strong style={{ color: rechargeTarget && (rechargeTarget.credits ?? 0) < 1000 ? 'var(--danger)' : 'var(--text)' }}>
            ¥{creditsToYuan(rechargeTarget?.credits ?? 0)}
          </strong>
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.rechargeAmountLabel')}</label>
          <input className="input" type="number" min="0.01" step="0.01" value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)} placeholder={t('users.rechargeAmountPlaceholder')} autoFocus />
          {(() => {
            const yuan = parseFloat(rechargeAmount);
            if (!yuan || yuan <= 0) return null;
            const rmbLower = `¥${yuan.toFixed(2)}`;
            // 人民币大写转换
            const toUpperRMB = (n: number): string => {
              const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
              const units = ['', '拾', '佰', '仟'];
              const bigUnits = ['', '万', '亿'];
              if (n === 0) return '零元整';
              const intPart = Math.floor(n);
              const decPart = Math.round((n - intPart) * 100);
              const jiao = Math.floor(decPart / 10);
              const fen = decPart % 10;
              let intStr = '';
              if (intPart > 0) {
                const s = intPart.toString();
                const groups: number[][] = [];
                for (let i = s.length; i > 0; i -= 4) {
                  const start = Math.max(0, i - 4);
                  groups.unshift(s.slice(start, i).split('').map(Number));
                }
                groups.forEach((g, gi) => {
                  let groupStr = '';
                  let zeroFlag = false;
                  let hasNonZero = false;
                  const padded = Array(4 - g.length).fill(0).concat(g);
                  padded.forEach((d, di) => {
                    if (d === 0) { if (hasNonZero) zeroFlag = true; }
                    else {
                      if (zeroFlag || (gi > 0 && !hasNonZero && intStr.length > 0)) { groupStr += '零'; zeroFlag = false; }
                      groupStr += digits[d] + units[3 - di];
                      hasNonZero = true;
                    }
                  });
                  if (groupStr) intStr += groupStr + bigUnits[groups.length - 1 - gi];
                });
                intStr += '元';
              }
              if (jiao === 0 && fen === 0) return intStr + '整';
              let decStr = '';
              if (jiao > 0) decStr += digits[jiao] + '角';
              else if (intPart > 0) decStr += '零';
              if (fen > 0) decStr += digits[fen] + '分';
              return intStr + decStr;
            };
            return (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--surface-2)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <div style={{ color: 'var(--text-2)' }}>{t('users.lowerAmount')}<strong style={{ color: 'var(--text)' }}>{rmbLower}</strong></div>
                <div style={{ color: 'var(--text-2)', marginTop: '0.25rem' }}>{t('users.upperAmount')}<strong style={{ color: 'var(--text)' }}>{toUpperRMB(yuan)}</strong></div>
              </div>
            );
          })()}
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.remark')}</label>
          <input className="input" value={rechargeRemark} onChange={e => setRechargeRemark(e.target.value)} placeholder={t('users.remarkPlaceholder')} />
        </div>
        {rechargeError && <p className="error-text">{rechargeError}</p>}
      </Modal>

      {/* Deduct modal */}
      <Modal
        open={showDeductModal && !!deductTarget}
        onClose={() => setShowDeductModal(false)}
        title={t('users.deductTitle', { username: deductTarget?.username || '' })}
        width="400px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowDeductModal(false)} disabled={deducting}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-danger" onClick={handleDeductSubmit} disabled={deducting}>{deducting ? t('users.deducting') : t('users.confirmDeduct')}</button>
          </>
        }
      >
        <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-2)' }}>
          {t('users.currentBalance')} <strong style={{ color: deductTarget && (deductTarget.credits ?? 0) < 1000 ? 'var(--danger)' : 'var(--text)' }}>
            ¥{creditsToYuan(deductTarget?.credits ?? 0)}
          </strong>
          {(() => {
            const yuan = parseFloat(deductAmount);
            if (!yuan || yuan <= 0 || !deductTarget) return null;
            const afterCredits = (deductTarget.credits ?? 0) - Math.round(yuan * 100);
            return (
              <span style={{ marginLeft: '0.75rem' }}>
                {t('users.afterDeduct')} <strong style={{ color: afterCredits < 0 ? 'var(--danger)' : 'var(--text)' }}>¥{creditsToYuan(afterCredits)}</strong>
              </span>
            );
          })()}
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.deductAmountLabel')}</label>
          <input className="input" type="number" min="0.01" step="0.01" value={deductAmount} onChange={e => setDeductAmount(e.target.value)} placeholder={t('users.deductAmountPlaceholder')} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">{t('users.deductReasonLabel')} <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('users.deductReasonAudit')}</span></label>
          <input className="input" value={deductRemark} onChange={e => setDeductRemark(e.target.value)} placeholder={t('users.deductReasonPlaceholder')} />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={deductAllowNegative} onChange={e => setDeductAllowNegative(e.target.checked)} />
            {t('users.allowNegativeBalance')}
          </label>
          {deductAllowNegative && (
            <div style={{ marginTop: '0.4rem', padding: '0.5rem 0.75rem', background: 'rgba(220, 38, 38, 0.08)', border: '1px solid var(--danger)', borderRadius: '6px', fontSize: '0.8rem', color: 'var(--danger)' }}>
              {t('users.negativeBalanceWarning')}
            </div>
          )}
        </div>
        {deductError && <p className="error-text">{deductError}</p>}
      </Modal>

      {/* Deduct confirm */}
      <ConfirmDialog
        open={confirmDeduct}
        onCancel={() => setConfirmDeduct(false)}
        onConfirm={doDeduct}
        title={t('users.confirmDeductTitle')}
        message={(() => {
          const yuan = parseFloat(deductAmount) || 0;
          const current = deductTarget?.credits ?? 0;
          const afterCredits = current - Math.round(yuan * 100);
          return (
            <div style={{ fontSize: '0.9rem', lineHeight: 1.7 }}>
              <div>{t('users.deductUser')}<strong>{deductTarget?.username}</strong></div>
              <div>{t('users.deductAmountConfirm')}<strong style={{ color: 'var(--danger)' }}>-¥{yuan.toFixed(2)}</strong></div>
              <div>{t('users.balanceAfterDeduct')}<strong style={{ color: afterCredits < 0 ? 'var(--danger)' : 'var(--text)' }}>¥{creditsToYuan(afterCredits)}</strong></div>
              <div>{t('users.deductReason')}{deductRemark.trim() || '-'}</div>
              {deductAllowNegative && afterCredits < 0 && (
                <div style={{ marginTop: '0.5rem', color: 'var(--danger)', fontSize: '0.85rem' }}>
                  {t('users.negativeBalanceNote')}
                </div>
              )}
            </div>
          );
        })()}
        confirmText={t('users.confirmDeduct')}
        confirmDanger
      />

      {/* Records modal */}
      <Modal
        open={showRecordsModal && !!recordsTarget}
        onClose={() => setShowRecordsModal(false)}
        title={t('users.recordsTitle', { username: recordsTarget?.username || '' })}
        width="720px"
        footer={<button className="btn btn-secondary" onClick={() => setShowRecordsModal(false)}>{t('close', { ns: 'common' })}</button>}
      >
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            className={`btn ${recordsTab === 'recharge' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
            onClick={() => setRecordsTab('recharge')}
          >{t('users.rechargeDeductRecords')}</button>
          <button
            className={`btn ${recordsTab === 'consumption' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
            onClick={() => setRecordsTab('consumption')}
          >{t('users.consumptionRecords')}</button>
        </div>

        {recordsLoading && consumptionRecords.length === 0 && rechargeRecords.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>{t('loading', { ns: 'common' })}</div>
        ) : recordsTab === 'recharge' ? (
          <div style={{ overflowX: 'auto', opacity: recordsLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            {rechargeRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>{t('users.noRechargeRecords')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={tblTh}>{t('users.time')}</th>
                    <th style={tblTh}>{t('users.operator')}</th>
                    <th style={tblTh}>{t('users.type')}</th>
                    <th style={{ ...tblTh, textAlign: 'right' }}>{t('users.changeAmount')}</th>
                    <th style={{ ...tblTh, textAlign: 'right' }}>{t('users.balanceAfter')}</th>
                    <th style={tblTh}>{t('users.remarkColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rechargeRecords.map(r => {
                    const isDeduct = r.type === 'deduct';
                    const sign = isDeduct ? '-' : '+';
                    const color = isDeduct ? 'var(--danger)' : 'var(--success)';
                    const badgeLabel = isDeduct ? t('users.deductBadge') : t('users.rechargeBadge');
                    const badgeBg = isDeduct ? 'var(--danger)' : 'var(--success)';
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tblTd}>{formatDate(r.createdAt)}</td>
                        <td style={tblTd}>{r.adminName}</td>
                        <td style={tblTd}>
                          <span style={{
                            background: badgeBg,
                            color: '#fff',
                            fontSize: '0.7rem',
                            padding: '1px 6px',
                            borderRadius: '10px',
                          }}>{badgeLabel}</span>
                        </td>
                        <td style={{ ...tblTd, textAlign: 'right', color, fontWeight: 500 }}>{sign}¥{creditsToYuan(r.amount)}</td>
                        <td style={{ ...tblTd, textAlign: 'right', color: r.balanceAfter < 0 ? 'var(--danger)' : undefined }}>¥{creditsToYuan(r.balanceAfter)}</td>
                        <td style={tblTd}>{r.remark || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {rechargeTotal > PAGE_SIZE && (
              <PaginationBar page={rechargePage} totalPages={Math.ceil(rechargeTotal / PAGE_SIZE)} loading={recordsLoading} onPageChange={(p) => recordsTarget && loadRecharges(recordsTarget.id, p)} prevLabel={t('users.prevPage')} nextLabel={t('users.nextPage')} />
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', opacity: recordsLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            {consumptionRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>{t('users.noConsumptionRecords')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={tblTh}>{t('users.time')}</th>
                    <th style={tblTh}>{t('users.operator')}</th>
                    <th style={tblTh}>{t('users.project')}</th>
                    <th style={tblTh}>{t('users.type')}</th>
                    <th style={tblTh}>{t('users.operation')}</th>
                    <th style={tblTh}>{t('users.model')}</th>
                    <th style={{ ...tblTh, textAlign: 'right' }}>{t('users.amountYuan')}</th>
                    <th style={{ ...tblTh, textAlign: 'right' }}>{t('users.balanceYuan')}</th>
                  </tr>
                </thead>
                <tbody>
                  {consumptionRecords.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tblTd}>{formatDate(r.createdAt)}</td>
                      <td style={tblTd}>{r.operatorName || '-'}</td>
                      <td style={tblTd}>{r.projectName}</td>
                      <td style={tblTd}>{t(`users.typeLabels.${r.operationType}`, { defaultValue: r.operationType })}</td>
                      <td style={tblTd}>{t(`users.operationLabels.${r.operationName}`, { defaultValue: r.operationName })}</td>
                      <td style={{ ...tblTd, fontSize: '0.78rem', color: 'var(--text-3)' }}>{r.modelName || '-'}</td>
                      <td style={{ ...tblTd, textAlign: 'right', color: 'var(--warning)', fontWeight: 500 }}>-¥{creditsToYuan(r.amount)}</td>
                      <td style={{ ...tblTd, textAlign: 'right' }}>¥{creditsToYuan(r.balanceAfter)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {consumptionTotal > PAGE_SIZE && (
              <PaginationBar page={consumptionPage} totalPages={Math.ceil(consumptionTotal / PAGE_SIZE)} loading={recordsLoading} onPageChange={(p) => recordsTarget && loadConsumptions(recordsTarget.id, p)} prevLabel={t('users.prevPage')} nextLabel={t('users.nextPage')} />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

function PaginationBar({ page, totalPages, loading, onPageChange, prevLabel, nextLabel }: { page: number; totalPages: number; loading: boolean; onPageChange: (p: number) => void; prevLabel: string; nextLabel: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem',
      borderTop: '1px solid var(--border)',
    }}>
      <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem' }} disabled={page <= 1 || loading} onClick={() => onPageChange(page - 1)}>
        {prevLabel}
      </button>
      <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{page} / {totalPages}</span>
      <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem' }} disabled={page >= totalPages || loading} onClick={() => onPageChange(page + 1)}>
        {nextLabel}
      </button>
    </div>
  );
}

const actionBtnStyle = (color?: string): React.CSSProperties => ({
  fontSize: '0.8rem',
  padding: '0.3rem 0.6rem',
  minWidth: '60px',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  ...(color ? { color } : {}),
});

const tblTh: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tblTd: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  whiteSpace: 'nowrap',
};

export default AdminUsers;
